import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { paths } from '@/utils/path'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, FormItem } from 'vnetwork-ui'
import { createAffiliate } from '../../services'
import { CreateAffiliateRequest } from '../../types'
import { createAffiliateSchema } from './schema'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    AFFILIATE_TIER_OPTIONS,
    DEFAULT_PAYOUT_THRESHOLD,
} from '../../constants'
import CustomerSelect from '@/components/Form/Shared/CustomerSelect'
import { useEffect } from 'react'

export interface AffiliateCreateFormValues {
    referrer_name: string
    referrer_email: string
    referrer_phone?: string
    referrer_company?: string
    customer_id?: number | null
    referral_code?: string
    tier: string
    /** Fallback rate, used only when an invoice matches no commission policy. Left blank
     *  here so the API decides the default — the client must not own that number. */
    commission_percent?: number
    payout_threshold: number
}

const New = () => {
    const { t } = useTranslation('affiliate')
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()

    const { control, handleSubmit, setValue } =
        useForm<AffiliateCreateFormValues>({
            defaultValues: {
                referrer_name: '',
                referrer_email: '',
                referrer_phone: '',
                referrer_company: '',
                customer_id: null,
                referral_code: '',
                tier: 'standard',
                commission_percent: undefined,
                payout_threshold: DEFAULT_PAYOUT_THRESHOLD,
            },
            resolver: yupResolver(createAffiliateSchema) as any,
            reValidateMode: 'onChange',
        })

    useEffect(() => {
        const customerID = Number(searchParams.get('customer_id'))
        if (customerID) {
            setValue('customer_id', customerID)
        }

        ;(
            [
                'referrer_name',
                'referrer_email',
                'referrer_phone',
                'referrer_company',
            ] as const
        ).forEach((field) => {
            const value = searchParams.get(field)
            if (value) {
                setValue(field, value)
            }
        })
    }, [searchParams, setValue])

    const { mutateAsync: submit, isPending } = useMutationWithHandler(
        {
            mutationFn: (data: CreateAffiliateRequest) => createAffiliate(data),
        },
        {
            successMessage: t('affiliate_created_successfully'),
            reactQueryKeys: QUERY_KEYS.AFFILIATES,
            onSuccess: () => {
                navigate(paths.affiliatesList)
            },
        }
    )

    const onSubmit = async (data: AffiliateCreateFormValues) => {
        await submit({
            referrer_name: data.referrer_name,
            referrer_email: data.referrer_email,
            referrer_phone: data.referrer_phone || undefined,
            referrer_company: data.referrer_company || undefined,
            customer_id: data.customer_id || undefined,
            referral_code: data.referral_code || undefined,
            tier: data.tier as any,
            commission_percent: data.commission_percent,
            payout_threshold: data.payout_threshold,
        })
    }

    return (
        <div>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.affiliatesList,
                        title: t('affiliate_manager'),
                    },
                    {
                        path: paths.affiliateCreate,
                        title: t('create_affiliate'),
                    },
                ]}
            />

            <Paper className="mt-4">
                <h2 className="text-lg font-semibold mb-4">
                    {t('create_affiliate')}
                </h2>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    data-tour="affiliate-form"
                >
                    <h3
                        className="text-sm font-medium text-gray-500 mb-3"
                        data-tour="affiliate-referrer-section"
                    >
                        {t('referrer_information')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div data-tour="affiliate-name">
                            <FormItem
                                control={control}
                                name="referrer_name"
                                label={t('referrer_name')}
                                type="textfield"
                            />
                        </div>

                        <div data-tour="affiliate-email">
                            <FormItem
                                control={control}
                                name="referrer_email"
                                label={t('email')}
                                type="textfield"
                            />
                        </div>

                        <div data-tour="affiliate-phone">
                            <FormItem
                                control={control}
                                name="referrer_phone"
                                label={t('phone')}
                                type="textfield"
                            />
                        </div>

                        <div data-tour="affiliate-company">
                            <FormItem
                                control={control}
                                name="referrer_company"
                                label={t('company')}
                                type="textfield"
                            />
                        </div>

                        <div data-tour="affiliate-customer">
                            <CustomerSelect
                                control={control}
                                name="customer_id"
                                label="link_to_customer"
                            />
                        </div>
                    </div>

                    <h3
                        className="text-sm font-medium text-gray-500 mb-3 mt-6"
                        data-tour="affiliate-commission-section"
                    >
                        {t('commission_settings')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div data-tour="affiliate-tier">
                            <FormItem
                                control={control}
                                name="tier"
                                label={t('tier')}
                                type="select"
                                options={AFFILIATE_TIER_OPTIONS(t)}
                            />
                        </div>

                        <div data-tour="affiliate-commission">
                            <FormItem
                                control={control}
                                name="commission_percent"
                                label={t('fallback_commission')}
                                type="number"
                                placeholder={t(
                                    'fallback_commission_placeholder'
                                )}
                                helperText={t(
                                    'fallback_commission_create_hint'
                                )}
                            />
                        </div>

                        <div data-tour="affiliate-payout">
                            <FormItem
                                control={control}
                                name="payout_threshold"
                                label={t('payout_threshold')}
                                type="number"
                            />
                        </div>

                        <div data-tour="affiliate-code">
                            <FormItem
                                control={control}
                                name="referral_code"
                                label={t('referral_code_optional')}
                                type="textfield"
                                placeholder={t('auto_generated_if_empty')}
                            />
                        </div>
                    </div>

                    <div
                        className="flex gap-2 justify-end mt-6"
                        data-tour="affiliate-submit"
                    >
                        <Button
                            variant="outlined"
                            onClick={() => navigate(paths.affiliatesList)}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            loading={isPending}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </form>
            </Paper>
        </div>
    )
}

export default New
